import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { AUTH_CONFIG, ERROR_MESSAGES } from '../config/constants';
import { logAuthState, validateUserData } from '../utils/authUtils';

// Import UI components
import GlassCard from '../components/ui/GlassCard';
import SkillsSelector from '../components/common/SkillsSelector';
import LocationSelector from '../components/common/LocationSelector';
import TitleSelector from '../components/common/TitleSelector';
import EducationDropdown from '../components/common/EducationDropdown';
import { Users, Mail, Check, ChevronRight, Briefcase, GraduationCap, MapPin, Phone } from 'lucide-react';

const CandidateCompleteProfilePage = () => {
    const { updateProfileCompletion, user, isAuthenticated, loading: authLoading, validateAuthState } = useContext(AuthContext);
    const navigate = useNavigate();
    
    // Validate user and redirect if needed
    useEffect(() => {
        const validateUser = async () => {
            try {
                // Skip validation if still loading auth state
                if (authLoading) return;
                
                // Log profile completion page access
                logAuthState('CANDIDATE_PROFILE_COMPLETION_ACCESS', { 
                    isAuthenticated,
                    userType: user?.userType,
                    profileCompleted: user?.profileCompleted
                });
                
                // Check if user exists and is authenticated
                if (!isAuthenticated || !user) {
                    logAuthState('CANDIDATE_PROFILE_COMPLETION_UNAUTHORIZED');
                    navigate('/auth/candidate');
                    return;
                }
                
                // Validate user data integrity
                const validatedUser = validateUserData(user);
                if (!validatedUser) {
                    logAuthState('CANDIDATE_PROFILE_COMPLETION_INVALID_USER');
                    navigate('/auth/candidate');
                    return;
                }
                
                // Check if user is a candidate
                if (validatedUser.userType !== 'candidate') {
                    logAuthState('CANDIDATE_PROFILE_COMPLETION_WRONG_USER_TYPE', { 
                        actualType: validatedUser.userType 
                    });
                    navigate('/employer/complete-profile', { replace: true });
                    return;
                }
                
                // Check if profile is already completed
                if (validatedUser.profileCompleted === true) {
                    logAuthState('CANDIDATE_PROFILE_ALREADY_COMPLETED');
                    navigate('/jobs', { replace: true });
                    return;
                }
                
                // Pre-fill form data from user object if available
                if (validatedUser.name || validatedUser.title || validatedUser.location) {
                    setFormData(prevData => ({
                        ...prevData,
                        name: validatedUser.name || prevData.name,
                        title: validatedUser.title || prevData.title,
                        location: validatedUser.location || prevData.location,
                        email: validatedUser.email || prevData.email
                    }));
                }
            } catch (err) {
                logAuthState('CANDIDATE_PROFILE_COMPLETION_ERROR', { error: err.message });
            }
        };
        
        validateUser();
    }, [user, navigate, isAuthenticated, authLoading]);
    const [formData, setFormData] = useState({
        name: '',
        title: '',
        location: '',
        phoneNumber: '',
        experience: '',
        skills: [],
        education: '',
        email: '' // Optional work email
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [step, setStep] = useState(1); // Track multi-step form progress
    const [agreeTerms, setAgreeTerms] = useState(false);
    
    // Form progress animation variants
    const progressVariants = {
        active: { scale: 1.1, backgroundColor: '#FBD149' },
        inactive: { scale: 1, backgroundColor: '#E5E7EB' },
        completed: { scale: 1, backgroundColor: '#000000' }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // Navigate to next step
    const handleNextStep = () => {
        // Step 1 validation with specific error messages
        if (step === 1) {
            if (!formData.name) {
                setError('Please enter your full name');
                return;
            }
            if (!formData.title) {
                setError('Please select your professional title');
                return;
            }
            if (!formData.location) {
                setError('Please select your location');
                return;
            }
            if (!formData.phoneNumber) {
                setError('Please enter your phone number');
                return;
            }
            if (!/^\d{10}$/.test(formData.phoneNumber)) {
                setError('Please enter a valid 10-digit phone number');
                return;
            }
        }
        
        // Step 2 validation with specific error messages
        if (step === 2) {
            if (!formData.experience) {
                setError('Please select your years of experience');
                return;
            }
            if (formData.skills.length === 0) {
                setError('Please add at least one skill');
                return;
            }
            if (!formData.education) {
                setError('Please enter your education qualification');
                return;
            }
        }
        
        // Step 3 validation
        if (step === 3 && !agreeTerms) {
            setError('Please agree to the terms and conditions');
            return;
        }
        
        setError('');
        if (step < 3) {
            setStep(step + 1);
        } else {
            handleSubmit();
        }
    };
    
    // Go back to previous step
    const handlePrevStep = () => {
        if (step > 1) {
            setStep(step - 1);
            setError('');
        }
    };
    
    const handleSubmit = useCallback(async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        
        // Log profile completion attempt
        logAuthState('CANDIDATE_PROFILE_COMPLETION_SUBMIT', { formData });
        
        try {
            // Validate user before proceeding
            if (!user || !isAuthenticated) {
                throw new Error('Authentication required');
            }
            
            // Ensure user type is set correctly
            if (user.userType !== 'candidate') {
                throw new Error('Invalid user type');
            }
            
            // Create updated user data with profileCompleted set to true
            const updatedUserData = {
                ...user,
                ...formData,
                userType: 'candidate', // Ensure this is a candidate profile
                profileCompleted: true, // Explicitly set profile as completed
                isPhoneVerified: true // Ensure phone is marked as verified
            };
            
            // Make API call
            const response = await api.post('/auth/complete-profile', updatedUserData);
            
            // Handle response
            const { user: responseUser, token: newToken } = response.data;
            
            // Save the new JWT token if provided
            if (newToken) {
                localStorage.setItem('authToken', newToken);
            }
            
            // Merge response data with our updated data to ensure we have complete information
            const finalUserData = {
                ...updatedUserData,
                ...(responseUser || {}), // Use API response data if available
                userType: 'candidate', // Ensure user type is correct
                profileCompleted: true // Ensure profile is marked as completed
            };
            
            // Log successful API response
            logAuthState('CANDIDATE_PROFILE_COMPLETION_API_SUCCESS', { 
                userId: finalUserData.id,
                hasApiResponse: !!responseUser
            });
            
            // Update the AuthContext using the updateProfileCompletion function
            if (typeof updateProfileCompletion === 'function') {
                const updateResult = await updateProfileCompletion(true, finalUserData);
                
                if (!updateResult) {
                    throw new Error('Failed to update profile completion status');
                }
                
                // Log successful profile update
                logAuthState('CANDIDATE_PROFILE_COMPLETION_SUCCESS');
            } else {
                throw new Error('Profile update function not available');
            }
            
            // Show success message
            setSuccess('Profile completed successfully! Redirecting to dashboard...');
            
            // Use React Router's navigate for a smoother transition after a short delay
            setTimeout(() => {
                navigate('/jobs', { replace: true });
            }, AUTH_CONFIG.TIMEOUTS.NAVIGATION_DELAY);
        } catch (err) {
            // Log the error
            logAuthState('CANDIDATE_PROFILE_COMPLETION_ERROR', { 
                error: err.message,
                isApiError: !!err.response
            });
            
            // Try offline mode if it's a network error
            if (!err.response || err.message.includes('network')) {
                try {
                    // Create updated user data
                    const offlineUserData = {
                        ...user,
                        ...formData,
                        userType: 'candidate',
                        profileCompleted: true,
                        isPhoneVerified: true,
                        updatedOffline: true, // Flag to indicate offline update
                        lastUpdated: new Date().toISOString()
                    };
                    
                    // Update the AuthContext
                    if (typeof updateProfileCompletion === 'function') {
                        const updateResult = await updateProfileCompletion(true, offlineUserData);
                        
                        if (updateResult) {
                            // Log offline success
                            logAuthState('CANDIDATE_PROFILE_COMPLETION_OFFLINE_SUCCESS');
                            
                            // Show success message
                            setSuccess('Profile saved in offline mode. Redirecting to dashboard...');
                            
                            // Navigate after a short delay
                            setTimeout(() => {
                                navigate('/jobs', { replace: true });
                            }, AUTH_CONFIG.TIMEOUTS.NAVIGATION_DELAY);
                            return;
                        }
                    }
                    
                    // If we get here, offline update failed
                    throw new Error('Failed to save profile in offline mode');
                } catch (offlineErr) {
                    // Set error for offline failure
                    setError('Failed to save profile: ' + (offlineErr.message || 'Unknown error'));
                    logAuthState('CANDIDATE_PROFILE_COMPLETION_OFFLINE_ERROR', { error: offlineErr.message });
                }
            } else {
                // Set error for API failure
                setError(err.response?.data?.message || err.message || 'Failed to complete profile');
            }
            
            setLoading(false);
        }
    }, [formData, user, isAuthenticated, navigate, updateProfileCompletion]);

    return (
        <div className="min-h-screen bg-gray-50 text-black flex items-center justify-center font-sans relative overflow-hidden py-10">
            {/* Background with subtle pattern */}
            <div className="absolute inset-0 bg-white opacity-90 z-0"></div>
            
            <GlassCard variant="light" className="w-full max-w-2xl mx-4 relative z-10 bg-white shadow-lg">
                {/* Progress indicator */}
                <div className="flex justify-center mb-8">
                    <div className="flex items-center">
                        {[1, 2, 3].map((stepNumber) => (
                            <React.Fragment key={stepNumber}>
                                <motion.div 
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                                    variants={progressVariants}
                                    animate={step > stepNumber ? 'completed' : step === stepNumber ? 'active' : 'inactive'}
                                >
                                    {step > stepNumber ? <Check size={16} /> : stepNumber}
                                </motion.div>
                                {stepNumber < 3 && (
                                    <div className={`w-12 h-1 ${step > stepNumber ? 'bg-black' : 'bg-gray-300'}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                
                <h2 className="text-3xl font-bold mb-2 text-black">
                    {step === 1 && "Let's get you started!"}
                    {step === 2 && "Tell us about your skills"}
                    {step === 3 && "Almost there!"}
                </h2>
                <p className="text-gray-600 mb-8">
                    {step === 1 && "Complete your profile to access the candidate dashboard"}
                    {step === 2 && "Help us match you with the right opportunities"}
                    {step === 3 && "Just a few more details before you're ready"}
                </p>
                
                <div className="space-y-6">
                    {/* Step 1: Basic Info */}
                    {step === 1 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Your full name</label>
                                <div className="relative">
                                    <input 
                                        id="name" 
                                        name="name" 
                                        type="text" 
                                        required 
                                        value={formData.name} 
                                        onChange={handleChange} 
                                        className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                                    />
                                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600">
                                        <Users size={18} />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="relative z-20">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Professional title <span className="text-red-500">*</span>
                                </label>
                                <TitleSelector
                                    selectedTitle={formData.title}
                                    onChange={(title) => setFormData(prev => ({ ...prev, title }))}
                                    required={true}
                                    placeholder="e.g. Frontend Developer"
                                />
                            </div>
                            
                            <div className="relative z-10">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location <span className="text-red-500">*</span>
                                </label>
                                <LocationSelector
                                    selectedLocation={formData.location}
                                    onChange={(location) => setFormData(prev => ({ ...prev, location }))}
                                    required={true}
                                    placeholder="Select your city or work preference..."
                                />
                            </div>
                            
                            <div>
                                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input 
                                        id="phoneNumber" 
                                        name="phoneNumber" 
                                        type="tel" 
                                        required
                                        maxLength="10"
                                        pattern="[0-9]{10}"
                                        placeholder="Enter 10-digit mobile number"
                                        value={formData.phoneNumber} 
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setFormData(prev => ({ ...prev, phoneNumber: value }));
                                        }}
                                        className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                                    />
                                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600">
                                        <Phone size={18} />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">We'll use this to contact you about opportunities</p>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Step 2: Skills and Experience */}
                    {step === 2 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-4">Years of experience</label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {['0-1', '1-3', '3-5', '5-10', '10-15', '15+'].map(exp => (
                                        <div 
                                            key={exp} 
                                            className={`p-4 text-center cursor-pointer transition-all ${formData.experience === exp 
                                                ? 'bg-black text-white border-2 border-black' 
                                                : 'bg-white text-black border border-gray-300'}`}
                                            onClick={() => setFormData({...formData, experience: exp})}
                                        >
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <Briefcase size={18} className={formData.experience === exp ? 'text-white' : 'text-gray-600'} />
                                                <span className="text-sm font-medium">{exp} years</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Key Skills <span className="text-red-500">*</span>
                                </label>
                                <SkillsSelector
                                    selectedSkills={formData.skills}
                                    onChange={(skills) => setFormData(prev => ({ ...prev, skills }))}
                                    maxSkills={10}
                                    required={true}
                                    placeholder="Search and add your skills..."
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Education <span className="text-red-500">*</span>
                                </label>
                                <EducationDropdown
                                    selectedEducation={formData.education}
                                    onChange={(education) => setFormData(prev => ({ ...prev, education }))}
                                    required={true}
                                />
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Step 3: Final Step */}
                    {step === 3 && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-6"
                        >
                            <div className="p-6 bg-white border border-gray-200">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-100 rounded-full">
                                            <Users size={20} className="text-black" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-black">Personal Details</h3>
                                            <p className="text-sm text-gray-600">{formData.name}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-100 rounded-full">
                                            <Briefcase size={20} className="text-black" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-black">Professional Title</h3>
                                            <p className="text-sm text-gray-600">{formData.title}</p>
                                        </div>
                                    </div>
                                    
                                    {formData.location && (
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-gray-100 rounded-full">
                                                <MapPin size={20} className="text-black" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-black">Location</h3>
                                                <p className="text-sm text-gray-600">{formData.location}</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {formData.phoneNumber && (
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-gray-100 rounded-full">
                                                <Phone size={20} className="text-black" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-black">Phone Number</h3>
                                                <p className="text-sm text-gray-600">+91 {formData.phoneNumber}</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-gray-100 rounded-full">
                                            <Briefcase size={20} className="text-black" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-black">Experience</h3>
                                            <p className="text-sm text-gray-600">{formData.experience} years</p>
                                        </div>
                                    </div>
                                    
                                    {formData.education && (
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-gray-100 rounded-full">
                                                <GraduationCap size={20} className="text-black" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-black">Education</h3>
                                                <p className="text-sm text-gray-600">{formData.education}</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email (Optional)</label>
                                        <div className="relative">
                                            <input 
                                                id="email" 
                                                name="email" 
                                                type="email" 
                                                value={formData.email} 
                                                onChange={handleChange} 
                                                className="w-full p-4 pl-12 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all" 
                                            />
                                            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600">
                                                <Mail size={18} />
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">We'll use this for important notifications</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center p-4 border-b border-gray-200">
                                <input 
                                    id="agree" 
                                    type="checkbox" 
                                    checked={agreeTerms}
                                    onChange={(e) => setAgreeTerms(e.target.checked)}
                                    className="h-5 w-5 rounded-md border-gray-300 text-black focus:ring-yellow-400 focus:ring-offset-0" 
                                />
                                <label htmlFor="agree" className="ml-3 block text-sm text-gray-700">
                                    I agree to JobLoop's <Link to="/terms" target="_blank" className="text-black font-medium border-b border-black hover:text-gray-700">Terms & Conditions</Link> and <Link to="/privacy" target="_blank" className="text-black font-medium border-b border-black hover:text-gray-700">Privacy Policy</Link>
                                </label>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Error Message */}
                    {error && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }} 
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="bg-white border-l-4 border-red-500 text-red-600 p-4 flex items-start gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm">{error}</p>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Success Message */}
                    {success && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }} 
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="bg-white border-l-4 border-green-500 text-green-600 p-4 flex items-start gap-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm">{success}</p>
                            </div>
                        </motion.div>
                    )}
                    
                    {/* Navigation Buttons */}
                    <div className="flex justify-between pt-4">
                        {step > 1 ? (
                            <button 
                                onClick={handlePrevStep}
                                className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors"
                            >
                                Back
                            </button>
                        ) : <div></div>}
                        
                        <button 
                            onClick={handleNextStep}
                            disabled={loading}
                            className="px-6 py-3 font-bold bg-black text-white border-2 border-black hover:bg-gray-900 transition-colors flex items-center gap-2"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </div>
                            ) : (
                                <>
                                    {step === 3 ? 'Complete Profile' : 'Continue'}
                                    {step !== 3 && <ChevronRight size={16} />}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </GlassCard>
        </div>
    );
};

export default CandidateCompleteProfilePage;
